import {
    ApplicationAccessType,
    CommunicationType,
    WhatsApp,
    WhatsAppButton,
    WhatsAppButtonsComponent,
    WhatsAppCarouselCard,
    WhatsAppCarouselComponent,
    WhatsAppComponent,
    WhatsAppLTOComponent,
    WhatsAppTemplateType,
    isButtonsComponent,
    isCarouselComponent,
    isLTOComponent,
} from 'domains/content/types';
import { DEFAULT_LANGUAGE_CODE } from 'models/languages';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';

import { transformComponentsWithMergeTags } from './utils/whatsAppUtils';
import { actionTypes } from './whatsAppActions';
import { WhatsAppContentStateTypes } from './whatsAppTypes';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeTemplateFields = (
    templateFields: string | Record<string, string> | undefined
): Record<string, string> => {
    if (!templateFields) return {};
    if (typeof templateFields === 'string') {
        try {
            return JSON.parse(templateFields);
        } catch {
            return {};
        }
    }
    return templateFields;
};

// ─── Initial state ────────────────────────────────────────────────────────────

export const whatsAppInitialState: WhatsAppContentStateTypes = {
    name: 'Untitled',
    components: [],
    language: 'en',
    templateFields: {},
    defaultTemplateData: {},
    applicationsAccess: [ApplicationAccessType.INJOURNEY],
    communicationType: CommunicationType.COMMERCIAL,
    status: ContentStatus.DRAFT,
    templateType: WhatsAppTemplateType.STANDARD,
    languages: [DEFAULT_LANGUAGE_CODE],
    selectedLanguage: DEFAULT_LANGUAGE_CODE,
    componentsByLanguage: { [DEFAULT_LANGUAGE_CODE]: [] },
};

// ─── Reducer ──────────────────────────────────────────────────────────────────

export const whatsAppReducer = (state: WhatsAppContentStateTypes, action: any): WhatsAppContentStateTypes => {
    switch (action.type) {
        case actionTypes.SET_NAME:
            return { ...state, name: action.name };

        case actionTypes.SET_BODY_TEXT: {
            const bodyIdx = state.components.findIndex((c) => c.type === 'BODY');
            const bodyComponent: WhatsAppComponent = { type: 'BODY', text: action.text || '' };
            const components =
                bodyIdx >= 0
                    ? state.components.map((c, i) => (i === bodyIdx ? bodyComponent : c))
                    : [...state.components, bodyComponent];
            return { ...state, components };
        }

        case actionTypes.SET_HEADER: {
            const withoutHeader = state.components.filter((c) => c.type !== 'HEADER');
            const components = action.header ? [action.header, ...withoutHeader] : withoutHeader;
            return { ...state, components };
        }

        case actionTypes.SET_FOOTER: {
            const withoutFooter = state.components.filter((c) => c.type !== 'FOOTER');
            const components = action.footer ? [...withoutFooter, action.footer] : withoutFooter;
            return { ...state, components };
        }

        case actionTypes.SET_LANGUAGE:
            return { ...state, language: action.language };

        case actionTypes.SET_TEMPLATE_TYPE:
            return { ...state, templateType: action.templateType, components: [] };

        case actionTypes.ADD_BUTTON: {
            const buttonsIdx = state.components.findIndex(isButtonsComponent);
            const prevButtons: WhatsAppButton[] =
                buttonsIdx >= 0 ? (state.components[buttonsIdx] as WhatsAppButtonsComponent).buttons : [];
            const updated: WhatsAppButtonsComponent = { type: 'BUTTONS', buttons: [...prevButtons, action.button] };
            const components =
                buttonsIdx >= 0
                    ? state.components.map((c, i) => (i === buttonsIdx ? updated : c))
                    : [...state.components, updated];
            return { ...state, components };
        }

        case actionTypes.UPDATE_BUTTON: {
            const buttonsIdx = state.components.findIndex(isButtonsComponent);
            if (buttonsIdx < 0) return state;
            const prev = state.components[buttonsIdx] as WhatsAppButtonsComponent;
            const buttons = prev.buttons.map((b, i) => (i === action.index ? action.button : b));
            const components = state.components.map((c, i) => (i === buttonsIdx ? { ...prev, buttons } : c));
            return { ...state, components };
        }

        case actionTypes.REMOVE_BUTTON: {
            const buttonsIdx = state.components.findIndex(isButtonsComponent);
            if (buttonsIdx < 0) return state;
            const prev = state.components[buttonsIdx] as WhatsAppButtonsComponent;
            const buttons = prev.buttons.filter((_, i) => i !== action.index);
            const components =
                buttons.length > 0
                    ? state.components.map((c, i) => (i === buttonsIdx ? { ...prev, buttons } : c))
                    : state.components.filter((_, i) => i !== buttonsIdx);
            return { ...state, components };
        }

        case actionTypes.REORDER_BUTTONS: {
            const buttonsIdx = state.components.findIndex(isButtonsComponent);
            if (buttonsIdx < 0) return state;
            const prev = state.components[buttonsIdx] as WhatsAppButtonsComponent;
            const components = state.components.map((c, i) =>
                i === buttonsIdx ? { ...prev, buttons: action.buttons } : c
            );
            return { ...state, components };
        }

        case actionTypes.ADD_CAROUSEL_CARD: {
            const carouselIdx = state.components.findIndex(isCarouselComponent);
            const prevCards: WhatsAppCarouselCard[] =
                carouselIdx >= 0 ? (state.components[carouselIdx] as WhatsAppCarouselComponent).cards : [];
            const updated: WhatsAppCarouselComponent = { type: 'CAROUSEL', cards: [...prevCards, action.card] };
            const components =
                carouselIdx >= 0
                    ? state.components.map((c, i) => (i === carouselIdx ? updated : c))
                    : [...state.components, updated];
            return { ...state, components };
        }

        case actionTypes.UPDATE_CAROUSEL_CARD: {
            const carouselIdx = state.components.findIndex(isCarouselComponent);
            if (carouselIdx < 0) return state;
            const prev = state.components[carouselIdx] as WhatsAppCarouselComponent;
            const cards = prev.cards.map((c, i) => (i === action.index ? action.card : c));
            const components = state.components.map((c, i) => (i === carouselIdx ? { ...prev, cards } : c));
            return { ...state, components };
        }

        case actionTypes.REMOVE_CAROUSEL_CARD: {
            const carouselIdx = state.components.findIndex(isCarouselComponent);
            if (carouselIdx < 0) return state;
            const prev = state.components[carouselIdx] as WhatsAppCarouselComponent;
            const cards = prev.cards.filter((_, i) => i !== action.index);
            const components = state.components.map((c, i) => (i === carouselIdx ? { ...prev, cards } : c));
            return { ...state, components };
        }

        case actionTypes.SET_LTO: {
            const ltoIdx = state.components.findIndex(isLTOComponent);
            const updated: WhatsAppLTOComponent = { type: 'LIMITED_TIME_OFFER', limited_time_offer: action.lto };
            const components =
                ltoIdx >= 0
                    ? state.components.map((c, i) => (i === ltoIdx ? updated : c))
                    : [...state.components, updated];
            return { ...state, components };
        }

        case actionTypes.SET_APPLICATIONS_ACCESS:
            return { ...state, applicationsAccess: action.applicationsAccess };

        case actionTypes.SET_COMMUNICATION_TYPE:
            return { ...state, communicationType: action.communicationType };

        case actionTypes.SET_WHATSAPP_STATE:
            return { ...action.whatsAppState };

        case actionTypes.SET_SELECTED_LANGUAGE: {
            const updatedMap = { ...state.componentsByLanguage, [state.selectedLanguage]: state.components };
            const newComponents = updatedMap[action.language] || [];
            return {
                ...state,
                componentsByLanguage: updatedMap,
                selectedLanguage: action.language,
                components: newComponents,
            };
        }

        case actionTypes.ADD_LANGUAGE:
            return {
                ...state,
                languages: [...state.languages, action.language],
                componentsByLanguage: { ...state.componentsByLanguage, [action.language]: [] },
            };

        case actionTypes.DELETE_LANGUAGE: {
            const { [action.language]: _removed, ...remainingMap } = state.componentsByLanguage;
            const newSelectedLanguage =
                action.language === state.selectedLanguage ? DEFAULT_LANGUAGE_CODE : state.selectedLanguage;
            return {
                ...state,
                languages: state.languages.filter((l) => l !== action.language),
                componentsByLanguage: remainingMap,
                selectedLanguage: newSelectedLanguage,
                components: remainingMap[newSelectedLanguage] ?? state.components,
            };
        }

        default:
            return state;
    }
};

// ─── State initializer ────────────────────────────────────────────────────────

export const initializeWhatsAppState = (
    whatsAppData: WhatsApp | Partial<WhatsApp> | null,
    isCopy: boolean
): WhatsAppContentStateTypes => {
    if (!whatsAppData || !('components' in whatsAppData && whatsAppData.components)) {
        return whatsAppInitialState;
    }

    const templateFields = normalizeTemplateFields(whatsAppData.templateFields);
    const applicationsAccess =
        typeof whatsAppData.applicationsAccess === 'string'
            ? (whatsAppData.applicationsAccess.split(',') as ApplicationAccessType[])
            : Array.isArray(whatsAppData.applicationsAccess)
            ? whatsAppData.applicationsAccess
            : [ApplicationAccessType.INJOURNEY];

    const components = whatsAppData.components || [];
    const transformedComponents = transformComponentsWithMergeTags(components, templateFields);

    const derivedTemplateType = components.some(isCarouselComponent)
        ? WhatsAppTemplateType.CAROUSEL
        : components.some(isLTOComponent)
        ? WhatsAppTemplateType.LTO
        : WhatsAppTemplateType.STANDARD;

    // Build per-language component map from componentsJson if available
    const componentsByLanguage: Record<string, WhatsAppComponent[]> = whatsAppData.componentsJson
        ? Object.fromEntries(
              Object.entries(whatsAppData.componentsJson).map(([lang, comps]) => [
                  lang,
                  transformComponentsWithMergeTags(comps, templateFields),
              ])
          )
        : { [DEFAULT_LANGUAGE_CODE]: transformedComponents };

    const languages = Object.keys(componentsByLanguage);
    const selectedLanguage = languages.includes(DEFAULT_LANGUAGE_CODE) ? DEFAULT_LANGUAGE_CODE : languages[0];
    const activeComponents = componentsByLanguage[selectedLanguage] || transformedComponents;

    return {
        name: isCopy ? `Copy of ${whatsAppData.name}` : whatsAppData.name || 'Untitled',
        components: activeComponents,
        language: whatsAppData.language || 'en',
        templateFields,
        defaultTemplateData: whatsAppData.defaultTemplateData || {},
        applicationsAccess,
        communicationType: whatsAppData.communicationType || CommunicationType.COMMERCIAL,
        status: whatsAppData.status || ContentStatus.DRAFT,
        templateType: derivedTemplateType,
        languages,
        selectedLanguage,
        componentsByLanguage,
        whatsappAccountId: whatsAppData.whatsappAccountId,
        whatsappNamespace: whatsAppData.whatsappNamespace,
        whatsappTemplateId: whatsAppData.whatsappTemplateId,
    };
};
